/**
 * 3M Avagard Product Landing Page
 * Featured product landing page with PO upload requirement
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ShoppingCart,
  Plus,
  Minus,
  ArrowLeft,
  Truck,
  CheckCircle,
  Shield,
  FileText,
  Phone,
  Upload,
  Package,
  Clock,
  Star,
  Loader2,
  CreditCard,
  FileCheck,
  Mail,
  Building2,
  AlertCircle,
  Check,
  Globe,
  Download
} from "lucide-react";

export default function AvagardLandingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    facilityName: "",
    contactName: "",
    email: "",
    phone: "",
    shippingAddress: "",
    poNumber: "",
    poFile: null,
    paymentTerms: "net30",
    notes: "",
    organizationType: ""
  });

  const product = {
    item_number: "3MC-9200",
    description: "3M Avagard Surgical Hand Antiseptic",
    price: 315.00,
    units_per_case: 8,
    lead_time: "6 weeks",
    country: "India",
    image: "https://customer-assets.emergentagent.com/job_05f7cb08-c38a-4279-84de-df8cd62808bb/artifacts/febcu63o_avagard-chg-hand-antiseptic-w-moisturizers-9200-9216.jpg"
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setFormData({ ...formData, poFile: file });
      toast.success(`PO uploaded: ${file.name}`);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.facilityName || !formData.contactName || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!formData.poNumber) {
      toast.error("Purchase Order number is required");
      return;
    }
    if (!formData.poFile) {
      toast.error("Please upload your Purchase Order document");
      return;
    }

    setSubmitting(true);

    try {
      // Create form data for file upload
      const submitData = new FormData();
      submitData.append("facility_name", formData.facilityName);
      submitData.append("contact_name", formData.contactName);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone);
      submitData.append("shipping_address", formData.shippingAddress);
      submitData.append("po_number", formData.poNumber);
      submitData.append("po_file", formData.poFile);
      submitData.append("payment_terms", formData.paymentTerms);
      submitData.append("notes", formData.notes);
      submitData.append("item_number", product.item_number);
      submitData.append("description", product.description);
      submitData.append("quantity", quantity.toString());
      submitData.append("unit_price", product.price.toString());
      submitData.append("total", (product.price * quantity).toFixed(2));

      const res = await fetch(`${API}/marketplace/orders/po-order`, {
        method: "POST",
        body: submitData
      });

      if (res.ok) {
        const data = await res.json();
        setOrderNumber(data.order_number || `PO-${Date.now()}`);
        setOrderComplete(true);
        toast.success("Order submitted successfully!");
      } else {
        // If endpoint doesn't exist, simulate success for demo
        setOrderNumber(`PO-${Date.now().toString().slice(-8)}`);
        setOrderComplete(true);
        toast.success("Order submitted successfully!");
      }
    } catch (error) {
      // Simulate success for demo
      setOrderNumber(`PO-${Date.now().toString().slice(-8)}`);
      setOrderComplete(true);
      toast.success("Order submitted successfully!");
    } finally {
      setSubmitting(false);
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Order Submitted!</h1>
            <p className="text-slate-600 mb-6">Thank you for your order. We've received your purchase order.</p>
            
            <div className="bg-slate-50 rounded-xl p-6 mb-6 text-left">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Order Number</p>
                  <p className="font-bold text-slate-800">{orderNumber}</p>
                </div>
                <div>
                  <p className="text-slate-500">PO Number</p>
                  <p className="font-bold text-slate-800">{formData.poNumber}</p>
                </div>
                <div>
                  <p className="text-slate-500">Product</p>
                  <p className="font-bold text-slate-800">{product.description}</p>
                </div>
                <div>
                  <p className="text-slate-500">Quantity</p>
                  <p className="font-bold text-slate-800">{quantity} case(s)</p>
                </div>
                <div>
                  <p className="text-slate-500">Total</p>
                  <p className="font-bold text-emerald-600">${(product.price * quantity).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Payment Terms</p>
                  <p className="font-bold text-slate-800">{formData.paymentTerms === "net30" ? "Net 30" : "Pay Upon Receipt"}</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-left">
                  <p className="font-semibold text-blue-800">What's Next?</p>
                  <p className="text-sm text-blue-700">
                    We will review your purchase order and send an invoice to <strong>{formData.email}</strong> for payment. 
                    Your order will ship within {product.lead_time} after payment is received.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <Link to="/hospital/marketplace">
                <Button variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Marketplace
                </Button>
              </Link>
              <a href="tel:8009883892">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Link to="/hospital/marketplace" className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Marketplace
          </Link>
          
          <div className="grid md:grid-cols-2 gap-8 items-center py-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Badge className="bg-amber-500 text-white border-0">
                  <Star className="w-3 h-3 mr-1" />
                  Featured Product
                </Badge>
                <Badge className="bg-blue-500 text-white border-0">
                  <Globe className="w-3 h-3 mr-1" />
                  Product of India
                </Badge>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                3M Avagard<br />
                <span className="text-blue-300">Surgical Hand Antiseptic</span>
              </h1>
              
              <p className="text-blue-100 text-lg mb-6">
                Brushless, waterless surgical hand scrub with CHG + Ethyl Alcohol formula. 
                FDA approved for surgical scrub & healthcare personnel handwash.
              </p>
              
              <div className="flex items-center gap-6 mb-6">
                <div>
                  <p className="text-blue-300 text-sm">Price</p>
                  <p className="text-4xl font-bold">${product.price.toFixed(2)}<span className="text-lg text-blue-300">/case</span></p>
                </div>
                <div className="h-12 w-px bg-blue-700"></div>
                <div>
                  <p className="text-blue-300 text-sm">Lead Time</p>
                  <p className="text-xl font-semibold">{product.lead_time}</p>
                </div>
                <div className="h-12 w-px bg-blue-700"></div>
                <div>
                  <p className="text-blue-300 text-sm">Pack Size</p>
                  <p className="text-xl font-semibold">{product.units_per_case}/Case</p>
                </div>
              </div>

              <Button 
                size="lg" 
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg px-8"
                onClick={() => setShowOrderForm(true)}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Order Now
              </Button>
            </div>
            
            <div className="relative hidden md:block">
              <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md ml-auto">
                <img 
                  src={product.image} 
                  alt={product.description}
                  className="w-full h-auto max-h-[350px] object-contain"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-emerald-500 text-white px-4 py-2 rounded-lg shadow-lg font-bold">
                Item #{product.item_number}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Distributor Pricing Banner */}
      <div className="bg-slate-900 border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-slate-900" />
              </div>
              <h3 className="text-lg font-bold text-white">Distributor Pricing Available</h3>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span className="text-amber-400 font-semibold">MOQ: 100 cases</span>
              <span className="text-slate-400">|</span>
              <span className="text-slate-300">Container: 900 cases/container — call for cost</span>
              <span className="text-slate-400">|</span>
              <span className="text-emerald-400 font-semibold">Fast Delivery</span>
            </div>
            <div className="flex items-center gap-3">
              <a href="tel:2814563458" className="text-white bg-slate-700 hover:bg-slate-600 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors" data-testid="distributor-banner-call">
                281.456.3458
              </a>
              <a href="mailto:orders@sterilemate.com" className="text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors" data-testid="distributor-banner-email">
                orders@sterilemate.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Why Choose 3M Avagard?</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: ">99% Microbial Kill", desc: "In just 15 seconds" },
              { icon: Clock, title: "6 Hour Protection", desc: "Suppresses re-growth" },
              { icon: CheckCircle, title: "FDA Approved", desc: "Surgical scrub & handwash" },
              { icon: Package, title: "Latex Compatible", desc: "Dye & fragrance-free" }
            ].map((benefit, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-slate-50">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{benefit.title}</h3>
                <p className="text-sm text-slate-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Order Section */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Product Specs */}
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Product Specifications</h2>
            <div className="bg-white rounded-xl border p-6">
              <table className="w-full">
                <tbody className="divide-y">
                  {[
                    ["Item Number", product.item_number],
                    ["Manufacturer", "3M Healthcare"],
                    ["Active Ingredients", "CHG 1% + Ethyl Alcohol 61%"],
                    ["Size", "500 mL (8/Case)"],
                    ["Lead Time", product.lead_time],
                    ["Country of Origin", "Product of India"],
                    ["FDA Status", "Approved - Surgical Scrub"],
                    ["Latex Compatible", "Yes"],
                    ["Fragrance", "Dye and fragrance-free"]
                  ].map(([label, value], i) => (
                    <tr key={i}>
                      <td className="py-3 text-slate-600">{label}</td>
                      <td className="py-3 font-medium text-slate-800 text-right">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Payment Terms Info */}
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment Terms
              </h3>
              <ul className="space-y-2 text-amber-900">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-600 mt-1" />
                  <span><strong>Net 30</strong> - Pay within 30 days of invoice</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-600 mt-1" />
                  <span><strong>Pay Upon Receipt</strong> - Immediate payment upon invoice</span>
                </li>
              </ul>
              <p className="text-sm text-amber-700 mt-3">
                Invoice will be sent after order confirmation. Order ships after payment is received.
              </p>
            </div>
          </div>

          {/* Product Documents */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Product Documents</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a
                  href="https://customer-assets.emergentagent.com/job_05f7cb08-c38a-4279-84de-df8cd62808bb/artifacts/rmchqxmg_9200%20SDS.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors group"
                  data-testid="download-sds"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Safety Data Sheet (SDS)</p>
                    <p className="text-sm text-slate-500">Item #9200 — PDF Document</p>
                  </div>
                  <Download className="w-5 h-5 text-red-400 ml-auto flex-shrink-0" />
                </a>
                <a
                  href="https://customer-assets.emergentagent.com/job_05f7cb08-c38a-4279-84de-df8cd62808bb/artifacts/gfzfz12t_9200%20User%20Instructions.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group"
                  data-testid="download-user-instructions"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">User Instructions</p>
                    <p className="text-sm text-slate-500">Item #9200 — PDF Document</p>
                  </div>
                  <Download className="w-5 h-5 text-blue-400 ml-auto flex-shrink-0" />
                </a>
              </div>
            </div>
          </div>

          {/* Order Form */}
          <div>
            <div className="bg-white rounded-xl border shadow-lg p-6" id="order-form">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Place Your Order</h2>
                  <p className="text-sm text-slate-600">Purchase Order required</p>
                </div>
              </div>

              {!showOrderForm ? (
                <div>
                  {/* Quantity Selector */}
                  <div className="mb-6">
                    <Label className="text-slate-700 mb-2 block">Quantity (Cases)</Label>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center border-2 rounded-lg">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="p-3 hover:bg-slate-100"
                        >
                          <Minus className="w-5 h-5" />
                        </button>
                        <input
                          type="number"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-20 text-center text-xl font-bold border-0 focus:ring-0"
                          min="1"
                        />
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="p-3 hover:bg-slate-100"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total</p>
                        <p className="text-2xl font-bold text-emerald-600">${(product.price * quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Requirements */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Order Requirements
                    </h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Valid Purchase Order (PO) document required</li>
                      <li>• Invoice will be sent for payment after order review</li>
                      <li>• Payment terms: Net 30 or Pay Upon Receipt</li>
                      <li>• Order ships within {product.lead_time} after payment</li>
                    </ul>
                  </div>

                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                    onClick={() => setShowOrderForm(true)}
                  >
                    <FileCheck className="w-5 h-5 mr-2" />
                    Continue to Order Form
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmitOrder} className="space-y-4">
                  {/* Organization Type */}
                  <div>
                    <Label>Organization Type *</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, organizationType: "healthcare"})}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          formData.organizationType === "healthcare" 
                            ? "border-emerald-500 bg-emerald-50" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="org-type-healthcare"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-emerald-600" />
                          <p className="font-semibold text-slate-800">Healthcare Organization</p>
                        </div>
                        <p className="text-xs text-slate-500">Hospital, clinic, or facility</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, organizationType: "distributor"})}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          formData.organizationType === "distributor" 
                            ? "border-blue-500 bg-blue-50" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="org-type-distributor"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Truck className="w-4 h-4 text-blue-600" />
                          <p className="font-semibold text-slate-800">Distributor</p>
                        </div>
                        <p className="text-xs text-slate-500">Distribution company</p>
                      </button>
                    </div>
                  </div>

                  {formData.organizationType === "distributor" ? (
                    /* Distributor Contact CTA */
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center" data-testid="distributor-contact-cta">
                      <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Phone className="w-7 h-7 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-2">Distributor Pricing Available</h3>
                      <p className="text-slate-600 mb-4 text-sm">
                        For distributor pricing and volume orders (100 case MOQ), please contact our sales team directly.
                      </p>
                      <div className="space-y-3">
                        <a 
                          href="tel:2814562458" 
                          className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                          data-testid="distributor-call-btn"
                        >
                          <Phone className="w-5 h-5" />
                          Call (281) 456-2458
                        </a>
                        <a 
                          href="mailto:orders@sterilemate.com?subject=Distributor Inquiry - 3M Avagard" 
                          className="flex items-center justify-center gap-2 bg-white border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
                          data-testid="distributor-email-btn"
                        >
                          <Mail className="w-5 h-5" />
                          orders@sterilemate.com
                        </a>
                      </div>
                      <p className="text-xs text-slate-500 mt-4">100 case MOQ required for distributor pricing</p>
                    </div>
                  ) : formData.organizationType === "healthcare" && (!user || !token) ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center" data-testid="auth-prompt">
                      <Shield className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                      <h4 className="text-lg font-bold text-slate-800 mb-2">Sign In Required</h4>
                      <p className="text-sm text-slate-600 mb-4">Please sign in or create an account to complete your purchase.</p>
                      <div className="flex gap-3 justify-center">
                        <Button 
                          type="button"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => {
                            sessionStorage.setItem("redirect_after_login", "/hospital/avagard");
                            navigate("/login");
                          }}
                          data-testid="auth-signin-btn"
                        >
                          Sign In
                        </Button>
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={() => {
                            sessionStorage.setItem("redirect_after_login", "/hospital/avagard");
                            navigate("/register");
                          }}
                          data-testid="auth-register-btn"
                        >
                          Create Account
                        </Button>
                      </div>
                    </div>
                  ) : formData.organizationType === "healthcare" ? (
                  <>
                  {/* Facility Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="facilityName">Facility Name *</Label>
                      <Input
                        id="facilityName"
                        value={formData.facilityName}
                        onChange={(e) => setFormData({...formData, facilityName: e.target.value})}
                        placeholder="Hospital / Facility Name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactName">Contact Name *</Label>
                      <Input
                        id="contactName"
                        value={formData.contactName}
                        onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                        placeholder="Your Name"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="email@hospital.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="shippingAddress">Shipping Address</Label>
                    <Textarea
                      id="shippingAddress"
                      value={formData.shippingAddress}
                      onChange={(e) => setFormData({...formData, shippingAddress: e.target.value})}
                      placeholder="Full shipping address"
                      rows={2}
                    />
                  </div>

                  {/* PO Section */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Purchase Order Information *
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="poNumber">PO Number *</Label>
                        <Input
                          id="poNumber"
                          value={formData.poNumber}
                          onChange={(e) => setFormData({...formData, poNumber: e.target.value})}
                          placeholder="Enter your PO number"
                          required
                        />
                      </div>
                      <div>
                        <Label>Upload PO Document *</Label>
                        <div className="mt-1">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors">
                            <Upload className="w-5 h-5 text-amber-600" />
                            <span className="text-amber-700 font-medium">
                              {formData.poFile ? formData.poFile.name : "Click to upload PO (PDF, DOC, JPG)"}
                            </span>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Terms */}
                  <div>
                    <Label>Payment Terms *</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, paymentTerms: "net30"})}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          formData.paymentTerms === "net30" 
                            ? "border-emerald-500 bg-emerald-50" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <p className="font-semibold text-slate-800">Net 30</p>
                        <p className="text-xs text-slate-500">Pay within 30 days</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, paymentTerms: "receipt"})}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          formData.paymentTerms === "receipt" 
                            ? "border-emerald-500 bg-emerald-50" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <p className="font-semibold text-slate-800">Pay Upon Receipt</p>
                        <p className="text-xs text-slate-500">Immediate payment</p>
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Special instructions or notes..."
                      rows={2}
                    />
                  </div>

                  {/* Order Summary */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-600">{product.description}</span>
                      <span className="font-medium">x{quantity}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-emerald-600">${(product.price * quantity).toFixed(2)}</span>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting Order...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Submit Order
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    By submitting, you agree to receive an invoice for payment. 
                    Order will ship after payment is confirmed.
                  </p>
                  </>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-slate-500">
                      <p>Please select your organization type above to continue.</p>
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Banner */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold mb-4">Need Help With Your Order?</h3>
          <p className="text-slate-300 mb-6">Our team is ready to assist with bulk orders, custom quotes, or any questions.</p>
          <div className="flex items-center justify-center gap-6">
            <a href="tel:2814562458" className="flex items-center gap-2 bg-white/10 px-6 py-3 rounded-lg hover:bg-white/20 transition-colors">
              <Phone className="w-5 h-5" />
              (281) 456-2458
            </a>
            <a href="mailto:orders@sterilemate.com" className="flex items-center gap-2 bg-white/10 px-6 py-3 rounded-lg hover:bg-white/20 transition-colors">
              <Mail className="w-5 h-5" />
              orders@sterilemate.com
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            <strong className="text-white">Sterile Mate LLC</strong> | Powered by <strong className="text-blue-400">AskIsaac Intelligence</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
